import { css } from '@emotion/core'
import { Heading, Text, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { isEmpty } from 'lodash'
import React from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoArrowDown } from '../../components/ConsultaSeguimentoArrowDown'
import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { ExemplosVerticalField, ExemplosVerticalFieldOptions } from '../../components/ExemplosVerticalField'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta12ExemplosEnum,
  ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum,
  ConsultaSeguimentoPergunta12Model,
} from './model-consultaSeguimentoPergunta12'
import {
  hasPergunta12ExemplosAtLeastTwoSim,
  hasPergunta12ExemplosFalhouAnySim,
  hasPergunta12ExemplosPassouAnySim,
  isPergunta12ExemplosAllFilled,
  isPergunta12ExemplosAllNao,
  isPergunta12ExemplosPassouFalhouAllFilled,
} from './utils-consultaSeguimentoPergunta12'

interface ConsultaSeguimentoPergunta12FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta12Model>
}

export function ConsultaSeguimentoPergunta12Field(props: Readonly<ConsultaSeguimentoPergunta12FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta12Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta12Model>({
    name,
    conditionToCalculate,
    calculateResultado,
  })

  const isPerguntaHeaderSim = value.PERGUNTA_12_HEADER === SimNaoEnum.SIM
  const hasPerguntaExemplosAtLeastTwoSim = hasPergunta12ExemplosAtLeastTwoSim(value?.PERGUNTA_12_EXEMPLOS)
  const isPerguntaExemplosAllNao = isPergunta12ExemplosAllNao(value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasPerguntaExemplosPassouAnySim = hasPergunta12ExemplosPassouAnySim(value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta12ExemplosFalhouAnySim(value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasSimOnlyInExemplosPassou = hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim
  const hasSimOnlyInExemplosFalhou = hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim

  const podeExibirTextoPassou = isPerguntaHeaderSim && hasPerguntaExemplosAtLeastTwoSim
  const textoBasePassou = hasSimOnlyInExemplosPassou
    ? 'Sim somente para exemplos Passou:'
    : 'Os mais frequentes são exemplos de Passou:'

  const textResultadoFalhouWithAnySim = hasSimOnlyInExemplosFalhou
    ? 'Sim somente para exemplos Falhou:'
    : 'Os mais frequentes são exemplos de Falhou:'
  const textResultadoFalhou = isPerguntaExemplosAllNao ? 'Não para todos' : textResultadoFalhouWithAnySim
  const textResultadoPassou = podeExibirTextoPassou ? textoBasePassou : ''

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_12}
          title={<ConsultaSeguimentoPerguntaHeaderTitle sufix='fica muito incomodado com barulhos do dia a dia?' />}
          status={value.status}
          field={<SimNaoSwitchButtonField name={name.PERGUNTA_12_HEADER} />}
        />
        {isPerguntaHeaderSim && (
          <VFlow>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
            </VFlow>
            <VFlow vSpacing={0}>
              <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta12ExemplosEnum>
                name={name.PERGUNTA_12_EXEMPLOS}
                nameOutro={name.OUTRO}
                label='A criança tem uma reação negativa ao som de...'
                description='Responda os exemplos até pontuar na Pergunta 12 ou avançar para o próximo fluxo'
                options={PERGUNTA_12_EXEMPLOS_OPTIONS}
                required
              />
              {hasPergunta12ExemplosAtLeastTwoSim(value?.PERGUNTA_12_EXEMPLOS) && (
                <VFlow>
                  <VFlow vSpacing={0}>
                    <VFlow style={styles.arrowDown}>
                      <ConsultaSeguimentoArrowDown />
                    </VFlow>
                    <ConsultaSeguimentoIndicatorHelper
                      title='Como ele reage a esses barulhos?'
                      subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                    />
                  </VFlow>
                  <VFlow vSpacing={0}>
                    <ExemplosVerticalField<ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum>
                      name={name.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU}
                      label='Responda todos os exemplos, conforme as respostas do genitor'
                      options={PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU_OPTIONS}
                      title='A criança...'
                      required
                    />

                    {hasPerguntaExemplosPassouAnySim && hasPerguntaExemplosFalhouAnySim && (
                      <VFlow>
                        <ConsultaSeguimentoIndicatorStep
                          text={
                            <VFlow vSpacing={0}>
                              <Text color='inherit'>Sim tanto para exemplos Passou quanto Falhou:</Text>
                              <Heading level={3} color='inherit'>
                                Pergunta sobre a frequência
                              </Heading>
                            </VFlow>
                          }
                          style={styles.indicatorStep}
                        />
                        <RadioGroupBoxField
                          name={name.PERGUNTA_12_FREQUENCIA}
                          label='Quais os que ele faz com mais frequência?'
                          options={PERGUNTA_12_FREQUENCIA_OPTIONS}
                          required
                        />
                      </VFlow>
                    )}
                  </VFlow>
                </VFlow>
              )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{
            FALHOU: textResultadoFalhou,
            PASSOU: textResultadoPassou,
          }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const conditionToCalculate = (values: ConsultaSeguimentoPergunta12Model): boolean => {
  if (values.PERGUNTA_12_HEADER === SimNaoEnum.NAO) {
    return true
  }

  if (!isPergunta12ExemplosAllFilled(values.PERGUNTA_12_EXEMPLOS)) {
    return false
  }

  if (!hasPergunta12ExemplosAtLeastTwoSim(values.PERGUNTA_12_EXEMPLOS)) {
    return true
  }

  if (!isPergunta12ExemplosPassouFalhouAllFilled(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)) {
    return false
  }

  const isPerguntaExemplosAllNao = isPergunta12ExemplosAllNao(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasPerguntaExemplosPassouAnySim = hasPergunta12ExemplosPassouAnySim(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta12ExemplosFalhouAnySim(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)

  if (
    isPerguntaExemplosAllNao ||
    (hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim) ||
    (hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim)
  ) {
    return true
  } else {
    return !isEmpty(values.PERGUNTA_12_FREQUENCIA)
  }
}

const calculateResultado = (values: ConsultaSeguimentoPergunta12Model): PassouFalhouEnum => {
  if (
    values.PERGUNTA_12_HEADER === SimNaoEnum.NAO ||
    !hasPergunta12ExemplosAtLeastTwoSim(values.PERGUNTA_12_EXEMPLOS)
  ) {
    return PassouFalhouEnum.PASSOU
  }

  const isPerguntaExemplosAllNao = isPergunta12ExemplosAllNao(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasPerguntaExemplosPassouAnySim = hasPergunta12ExemplosPassouAnySim(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta12ExemplosFalhouAnySim(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)

  if (hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim) {
    return PassouFalhouEnum.PASSOU
  } else if ((hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim) || isPerguntaExemplosAllNao) {
    return PassouFalhouEnum.FALHOU
  } else {
    return values.PERGUNTA_12_FREQUENCIA
  }
}

const PERGUNTA_12_EXEMPLOS_OPTIONS: RadioOption<ConsultaSeguimentoPergunta12ExemplosEnum>[] = [
  {
    label: 'Máquina de lavar?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.MAQUINA_DE_LAVAR,
  },
  {
    label: 'Choro de bebês?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.CHORO_DE_BEBES,
  },
  {
    label: 'Aspirador de pó?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.ASPIRADOR_DE_PO,
  },
  {
    label: 'Secador de cabelo?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.SECADOR_DE_CABELO,
  },
  {
    label: 'Trânsito?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.TRANSITO,
  },
  {
    label: 'Grito de bebês?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.GRITO_DE_BEBES,
  },
  {
    label: 'Música alta?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.MUSICA_ALTA,
  },
  {
    label: 'Toque do telefone/campainha?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.TOQUE_TELEFONE_CAMPAINHA,
  },
  {
    label: 'Barulho de liquidificador?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.BARULHO_DE_LIQUIDIFICADOR,
  },
  {
    label: 'Lugares barulhentos como supermercado ou restaurante?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.LUGARES_BARULHENTOS,
  },
]

const PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU_OPTIONS: ExemplosVerticalFieldOptions<ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum> = {
  PASSOU: [
    {
      label: 'Tampa os ouvidos calmamente?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.TAMPA_OUVIDOS_CALMAMENTE,
    },
    {
      label: 'Diz a você que não gosta de barulho?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.NAO_GOSTA_BARULHO,
    },
  ],
  FALHOU: [
    {
      label: 'Grita?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.GRITA,
    },
    {
      label: 'Chora?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.CHORA,
    },
    {
      label: 'Tampa os ouvidos?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.TAMPA_OUVIDOS,
    },
  ],
}

const PERGUNTA_12_FREQUENCIA_OPTIONS: RadioOption<PassouFalhouEnum>[] = [
  {
    label: 'Exemplos PASSOU',
    value: PassouFalhouEnum.PASSOU,
  },
  {
    label: 'Exemplos FALHOU',
    value: PassouFalhouEnum.FALHOU,
  },
]

const styles = {
  indicatorStep: css`
    width: auto;
  `,
  arrowDown: css`
    display: grid;
    justify-self: center;
    justify-items: center;
  `,
}
